import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import moment from 'moment';
import PropTypes from 'prop-types';
 import { makeStyles } from '@material-ui/styles';
 import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import {
  Button,
  Card,
  Avatar,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  MenuItem,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  colors
} from '@material-ui/core';

import { Label,  TableEditBar } from '../../../../components';

const useStyles = makeStyles(theme => ({
  root: {},
  filterButton: {
    marginRight: theme.spacing(2)
  },
  content: {
    padding: 0
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(2, 2, 1)
  },
  filterField: {
    minWidth: 170,
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  inner: {
    width: '100%',
    overflowX: 'auto'
  },
  table: {
    minWidth: 1500
  },
  nowrap: {
    whiteSpace: 'nowrap'
  },
  actions: {
    padding: theme.spacing(0, 1),
    justifyContent: 'flex-end'
  }
}));

const FILTER_KEY = 'manageads.filters';
const MODULES = ['live', 'rate', 'news', 'spot', 'future', 'homepage'];
const AD_TYPES = ['banner', 'listing', 'detail'];

function readFilters() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(FILTER_KEY) || '');
    return saved && typeof saved === 'object' ? saved : {};
  } catch (error) {
    return {};
  }
}

function namedOptions(list, pick) {
  const seen = new Map();
  list.forEach(item => {
    const value = pick(item);
    if (!value || value.id == null || !value.name) {
      return;
    }
    seen.set(String(value.id), value.name);
  });
  return Array.from(seen, ([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function stringOptions(list, key, extras) {
  const values = new Set(extras);
  list.forEach(item => {
    if (item && item[key]) {
      values.add(String(item[key]));
    }
  });
  return Array.from(values).sort((a, b) => a.localeCompare(b));
}

const Results = props => {
  const { className, orders, searchtext, ...rest } = props;
  const savedFilters = readFilters();

  const classes = useStyles();

  const [selectedOrders, setSelectedOrders] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [commodity, setCommodity] = useState(savedFilters.commodity || '');
  const [subcommodity, setSubcommodity] = useState(savedFilters.subcommodity || '');
  const [moduleType, setModuleType] = useState(savedFilters.module || '');
  const [adType, setAdType] = useState(savedFilters.adtype || '');
  const [schedule, setSchedule] = useState(savedFilters.schedule || '');
  const [status, setStatus] = useState(savedFilters.status || 'active');
  const [showpreview, setShowpreview] = useState(false);
  const [previewurl, setpreviewurl] = useState('');

  const handleSelectAll = event => {
    const selectedOrders = event.target.checked
      ? orders.map(order => order.id)
      : [];

    setSelectedOrders(selectedOrders);
  };

  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedOrders.indexOf(id);
    let newSelectedOrders = [];

    if (selectedIndex === -1) {
      newSelectedOrders = newSelectedOrders.concat(selectedOrders, id);
    } else if (selectedIndex === 0) {
      newSelectedOrders = newSelectedOrders.concat(selectedOrders.slice(1));
    } else if (selectedIndex === selectedOrders.length - 1) {
      newSelectedOrders = newSelectedOrders.concat(selectedOrders.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedOrders = newSelectedOrders.concat(
        selectedOrders.slice(0, selectedIndex),
        selectedOrders.slice(selectedIndex + 1)
      );
    }

    setSelectedOrders(newSelectedOrders);
  };

  const handleChangePage = (event, nextPage) => {
    setPage(nextPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const commodityOptions = useMemo(
    () => namedOptions(orders, order => order.parentcommodity),
    [orders]
  );
  const subcommodityOptions = useMemo(
    () => namedOptions(
      orders.filter(order => !commodity || String(order.parentcommodity && order.parentcommodity.id) === String(commodity)),
      order => order.subcommodity
    ),
    [orders, commodity]
  );
  const moduleOptions = useMemo(() => stringOptions(orders, 'module', MODULES), [orders]);
  const adTypeOptions = useMemo(() => stringOptions(orders, 'adtype', AD_TYPES), [orders]);

  const filteredOrders = useMemo(() => {
    const query = (searchtext || '').trim().toLowerCase();
    return orders
      .filter(order => {
        if (status === 'active' && !order.status) {
          return false;
        }
        if (status === 'inactive' && order.status) {
          return false;
        }
        if (commodity && String(order.parentcommodity && order.parentcommodity.id) !== String(commodity)) {
          return false;
        }
        if (subcommodity && String(order.subcommodity && order.subcommodity.id) !== String(subcommodity)) {
          return false;
        }
        if (moduleType && order.module !== moduleType) {
          return false;
        }
        if (adType && order.adtype !== adType) {
          return false;
        }
        if (schedule === 'period' && order.schedule_mode !== 'period') {
          return false;
        }
        if (schedule === 'all_time' && order.schedule_mode === 'period') {
          return false;
        }
        if (!query) {
          return true;
        }
        const blob = [
          order.ads_url,
          order.module,
          order.adtype,
          order.priority,
          order.parentcommodity && order.parentcommodity.name,
          order.subcommodity && order.subcommodity.name
        ].filter(value => value != null).join(' ').toLowerCase();
        return blob.includes(query);
      })
      .sort((a, b) => {
        const recentA = new Date(a.createdAt || 0).getTime();
        const recentB = new Date(b.createdAt || 0).getTime();
        if (recentB !== recentA) {
          return recentB - recentA;
        }
        return (b.id || 0) - (a.id || 0);
      });
  }, [orders, searchtext, status, commodity, subcommodity, moduleType, adType, schedule]);

  useEffect(() => {
    sessionStorage.setItem(FILTER_KEY, JSON.stringify({
      commodity,
      subcommodity,
      module: moduleType,
      adtype: adType,
      schedule,
      status
    }));
  }, [commodity, subcommodity, moduleType, adType, schedule, status]);

  useEffect(() => {
    if (!orders.length) {
      return;
    }
    if (commodity && !commodityOptions.some(option => option.id === String(commodity))) {
      setCommodity('');
    }
    if (subcommodity && !subcommodityOptions.some(option => option.id === String(subcommodity))) {
      setSubcommodity('');
    }
    if (moduleType && !moduleOptions.includes(moduleType)) {
      setModuleType('');
    }
    if (adType && !adTypeOptions.includes(adType)) {
      setAdType('');
    }
  }, [orders, commodity, commodityOptions, subcommodity, subcommodityOptions, moduleType, moduleOptions, adType, adTypeOptions]);

  useEffect(() => {
    setPage(0);
  }, [searchtext, status, commodity, subcommodity, moduleType, adType, schedule]);

  const visibleOrders = filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  function previewimage(url)
  {
    setpreviewurl(url)
    setShowpreview(true)
  }
  const paymentStatusColors = {
    canceled: colors.grey[600],
    pending: colors.orange[600],
    active: colors.green[600],
    inactive: colors.red[600]
  };


  return (
    <>
    {showpreview && (
          <Lightbox
            mainSrc={previewurl}
           // nextSrc={images[(photoIndex + 1) % images.length]}
           // prevSrc={images[(photoIndex + images.length - 1) % images.length]}
            onCloseRequest={() => setShowpreview(false)}
            onMovePrevRequest={() =>
              alert('prev')
            }
            onMoveNextRequest={() =>
              alert('next')
            }
          />
        )}
    <div
      {...rest}
      className={clsx(classes.root, className)}
    >
     
      <Card>
        <CardHeader
          
          title="ADs"
        />
        <Divider />
        <div className={classes.filters}>
          <TextField
            className={classes.filterField}
            label="Commodity"
            onChange={event => setCommodity(event.target.value)}
            select
            value={commodity}
            variant="outlined"
          >
            <MenuItem value="">All</MenuItem>
            {commodityOptions.map(option => (
              <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            className={classes.filterField}
            label="Sub commodity"
            onChange={event => setSubcommodity(event.target.value)}
            select
            value={subcommodityOptions.some(option => option.id === String(subcommodity)) ? subcommodity : ''}
            variant="outlined"
          >
            <MenuItem value="">All</MenuItem>
            {subcommodityOptions.map(option => (
              <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            className={classes.filterField}
            label="Module type"
            onChange={event => setModuleType(event.target.value)}
            select
            value={moduleType}
            variant="outlined"
          >
            <MenuItem value="">All</MenuItem>
            {moduleOptions.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
          <TextField
            className={classes.filterField}
            label="Ad type"
            onChange={event => setAdType(event.target.value)}
            select
            value={adType}
            variant="outlined"
          >
            <MenuItem value="">All</MenuItem>
            {adTypeOptions.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
          <TextField
            className={classes.filterField}
            label="Schedule"
            onChange={event => setSchedule(event.target.value)}
            select
            value={schedule}
            variant="outlined"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="all_time">All time</MenuItem>
            <MenuItem value="period">Period</MenuItem>
          </TextField>
          <TextField
            className={classes.filterField}
            label="Status"
            onChange={event => setStatus(event.target.value)}
            select
            value={status}
            variant="outlined"
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">In Active</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </TextField>
        </div>
        <CardContent className={classes.content}>
            <div className={classes.inner}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    {/* <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedOrders.length === orders.length}
                        color="primary"
                        indeterminate={
                          selectedOrders.length > 0 &&
                          selectedOrders.length < orders.length
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell> */}
                    <TableCell>S no</TableCell>
                    <TableCell>Ad Image</TableCell>
                    <TableCell>Url</TableCell>
                    <TableCell>Commodity</TableCell>
                    <TableCell>Sub Commodity</TableCell>
                    {/* <TableCell>Language</TableCell> */}
                    <TableCell>Module Type</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Ad type</TableCell>
                    <TableCell>Schedule</TableCell>
                    <TableCell>status</TableCell>
                    <TableCell>CreatedAt</TableCell>
                    <TableCell align="center">Edit</TableCell>
                    {/* <TableCell align="center">View</TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleOrders.map((order,index) => (
                    <TableRow
                      key={order.id}
                      selected={selectedOrders.indexOf(order.id) !== -1}
                    >
                      {/* <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedOrders.indexOf(order.id) !== -1}
                          color="primary"
                          onChange={event => handleSelectOne(event, order.id)}
                          value={selectedOrders.indexOf(order.id) !== -1}
                        />
                      </TableCell> */}
                      <TableCell>
                      {(page * rowsPerPage)+(index+1)}
                        
                      </TableCell>

                      <TableCell>
                        
                        <Avatar alt={order.title} src={order.imageurl} onClick={() => previewimage(order.imageurl)} />
                      </TableCell>
                      <TableCell className={classes.nowrap}>{order.ads_url}</TableCell>
                      
                      <TableCell>
                          <Label
                          color={paymentStatusColors['canceled']}
                          variant="contained"
                        >
                          {order?.parentcommodity?.name}
                        </Label>
                     </TableCell>
                      <TableCell>
                          <Label
                          color={paymentStatusColors['canceled']}
                          variant="contained"
                        >
                          {order?.subcommodity?.name}
                        </Label>
                     </TableCell>
                      <TableCell className={classes.nowrap}>{order.module}</TableCell>
                      <TableCell>{order.priority}</TableCell>
                      <TableCell className={classes.nowrap}>{order.adtype}</TableCell>
                      <TableCell>
                        {(order.schedule_mode === 'period' && order.start_at && order.end_at)
                          ? `${moment(order.start_at).format('DD MMM YY HH:mm')} – ${moment(order.end_at).format('DD MMM YY HH:mm')}`
                          : 'All time'}
                      </TableCell>
                      
                      <TableCell>
                         <Label
                          color={paymentStatusColors[order.status ? 'active' : 'inactive']}
                          variant="contained"
                        >
                          {order.status ? 'Active' : 'In Active'}
                        </Label>

                      </TableCell>
                      <TableCell>
                      <Typography variant="body1">
                          {moment(order.createdAt).format(
                            'DD MMM YYYY | hh:mm a'
                          )}
                        </Typography>
                      </TableCell>
                     
                      <TableCell align="center">
                        <Button
                          color="primary"
                          component={RouterLink}
                          size="small"
                          to={`/editad/${order.id}`}
                          variant="outlined"
                          // to={`/editsurvey/${order.id}`}
                        >
                          Edit
                        </Button>
                      </TableCell>
                      {/* <TableCell align="center">
                        <Button
                          color="primary"
                          component={RouterLink}
                          size="small"
                          to={`/editad/${order.id}`}
                          variant="outlined"
                        >
                         View
                        </Button>
                      </TableCell> */}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
        </CardContent>
        <CardActions className={classes.actions}>
          <TablePagination
            component="div"
            count={filteredOrders.length}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25,100]}
          />
        </CardActions>
      </Card>
      <TableEditBar selected={selectedOrders} />
    </div>
    </>
  );
};

Results.propTypes = {
  className: PropTypes.string,
  orders: PropTypes.array.isRequired,
  searchtext: PropTypes.string
};

Results.defaultProps = {
  orders: []
};

export default Results;
